// FASE 05 — S4-B2/PO 2026-05-13: tipos para a tela de Tabelas de Precos (Materiais).
// Decisao D1.b: o CRUD vive no backend legado (api/MOF.Server). Estas interfaces
// modelam o contrato consumido pelo frontend novo via apiAntigo.

// Tipos de material (espelho do enum MaterialEnum.Tipo do legado).
// Valores numericos batem com MaterialEnum.Tipo do legado.
export type TipoMaterialTP =
  | "perfis"
  | "componentes"
  | "kits"
  | "acabamentos"
  | "superficies"
  | "vidros";

export const TIPO_MATERIAL_TP_LABELS: Record<TipoMaterialTP, string> = {
  perfis: "PERFIS",
  componentes: "COMPONENTES",
  kits: "KITS",
  acabamentos: "ACABAMENTOS",
  superficies: "SUPERFÍCIES",
  vidros: "VIDROS",
};

// Mapeamento UI -> Enum numerico esperado pelo backend legado (MaterialEnum.Tipo
// em api/MOF.DataServices/Enums/MaterialEnum.cs). Atencao: o enum do legado tem
// KIT = 6 (diferente do backend novo, que usa Kit = 9). Como o consumo deste
// modulo e via apiAntigo, usamos os valores do legado.
export const TIPO_MATERIAL_TP_ENUM: Record<TipoMaterialTP, number> = {
  perfis: 1,
  componentes: 2,
  vidros: 3,
  superficies: 4,
  acabamentos: 5,
  kits: 6,
};

// FASE 05 — decisao TP-1 do PO (19/08/2026, docs/DECISOES.md):
// o modal "Adicionar nova tabela" deixou de pedir Referencia. A coluna
// TabelasPrecos.IdReferencia passa a receber sempre 36 (MERCADO).
// Ela e decorativa no fluxo de precificacao — o universo de produtos vem de TODAS
// as referencias liberadas da empresa, nunca desta coluna — e o XML que o CEM
// Desktop consome nao carrega referencia, so empresa.
export const ID_REFERENCIA_PADRAO_TABELA_PRECO = 36;

// Situacao (SituacaoEnum.TabelaPreco no legado).
export type SituacaoTabelaPreco =
  | "EmEdicao"
  | "EmHomologacao"
  | "Disponivel"
  | "Indisponivel";

export const SITUACAO_LABELS: Record<SituacaoTabelaPreco, string> = {
  EmEdicao: "Em edição",
  EmHomologacao: "Em homologação",
  Disponivel: "Disponível Mercado",
  Indisponivel: "Indisponível",
};


// Valores do enum SituacaoEnum.TabelaPreco do legado.
// FASE 05 — decisao TP-2 do PO (26/08/2026): "Homologada" e um estado novo. Ciclo:
// Em Edicao -[Homologar]-> Em Homologacao -[Concluir Homologacao]-> Homologada
//           -[Disponibilizar Mercado]-> Disponivel Mercado.
export enum SituacaoTabelaPrecoEnum {
  EmEdicao = 1,
  EmHomologacao = 2,
  Disponivel = 3,
  Desconsiderado = 4,
  Homologada = 5,
}

// Rótulos por valor numérico — usados no chip do grid e no cabeçalho da edição.
export const SITUACAO_DESCRICAO: Record<number, string> = {
  [SituacaoTabelaPrecoEnum.EmEdicao]: "Em Edição",
  [SituacaoTabelaPrecoEnum.EmHomologacao]: "Em Homologação",
  [SituacaoTabelaPrecoEnum.Disponivel]: "Disponível para Mercado",
  [SituacaoTabelaPrecoEnum.Desconsiderado]: "Desconsiderado",
  [SituacaoTabelaPrecoEnum.Homologada]: "Homologada",
};

// StatusEnum.Material do legado — as duas opcoes do filtro pedido no item 12.
export enum StatusProdutoEnum {
  Ativo = 1,
  Descontinuado = 2,
}

export const STATUS_PRODUTO_LABELS: Record<number, string> = {
  [StatusProdutoEnum.Ativo]: "Ativo",
  [StatusProdutoEnum.Descontinuado]: "Descontinuado",
};

// Tipo de produto liberado para a empresa (item 1 do reteste de 26/08).
export interface ITipoLiberado {
  idTipoMaterial: number;
  tipo: string;
  descricao: string;
}

// Filtros do grid de precificacao — resolvidos no servidor (TP-6).
export interface IFiltroPrecificacao {
  pagina: number;
  tamanhoPagina: number;
  codigo?: string;
  descricao?: string;
  fornecedor?: string;
  // Filtros das colunas novas (TP-16): Linha/Sistema nos Perfis, Tipo nos Kits.
  linha?: string;
  tipoKit?: string;
  status?: number | null;
  versaoPublicada?: boolean;
  somenteComPreco?: boolean;
}

// Item da listagem (rota GET /api/TabelaPreco/Lista do legado).
// Backend retorna campos pascal-case via JSON; o service mapeia para camel-case.
export interface ITabelaPrecoListagem {
  id: number;
  empresa?: string;
  referencia: string;
  nome: string;
  tipoProduto: string;
  dataAtualizacao: string;
  situacao: string;
  // Reporte TP 02/09 (item 1): existe lote publicado — o switch OFF exibe a tabela como
  // Disponivel Mercado (ConsultaPublicada) mesmo com edicao em andamento.
  possuiVersaoPublicada?: boolean;
}

// Payload para criar uma nova tabela (POST /api/TabelaPreco — Salvar).
export interface INovaTabelaPrecoPayload {
  nome: string;
  idTipoMaterial: number;
  idReferencia: number;
}

// FASE 05 — S4-B3/B4/B5: contratos da pagina de precificacao
// (GET /api/TabelaPreco/ItensPrecificacao/{id} do legado).
export interface IItemPrecificacao {
  chave: string;
  idProduto: number;
  codigo: string;
  cor: string | null;
  descricao: string;
  fornecedor: string | null;
  grupo: string | null;
  // Colunas novas dos tipos do plano de 28/08 — vêm vazias onde não se aplicam.
  linha: string | null;
  peso: number;
  tipoKit: string | null;
  unidadeItem: string | null;
  unidadeEmbalagem: string | null;
  quantidadeEmbalagem: number;
  ipi: number;
  // Item 11 do reteste de 26/08: a coluna "Situação" deu lugar a "Status".
  status: string;
  preco: number;
  precoItem: number;
  // Plano 30/08/2026, Frente 10 (TP-20): só Acabamentos. null = taxa não informada.
  taxa: number | null;
  // Plano 30/08/2026, Frente 11 (TP-18): só Kits — o preço passou a ser por kit+medida+cor.
  idMedidaCores: number | null;
  idCor: number | null;
  medida: string | null;
  // Plano 08/09/2026: só Perfis com unidade BR — uma linha por tamanho de barra, preço próprio.
  tamanhoBarra: number | null;
}

export interface IPrecificacao {
  idTabelaPreco: number;
  nome: string;
  empresa: string | null;
  referencia: string | null;
  tipoMaterial: number;
  tipoProduto: string;
  situacao: SituacaoTabelaPrecoEnum;
  situacaoDescricao: string;
  podeEditar: boolean;
  somenteConsulta: boolean;
  precificacaoSuportada: boolean;
  motivoNaoSuportada: string | null;
  // TP-8: só habilita "Homologar" com a tabela Em Edição E com ao menos um preço gravado.
  possuiPrecoGravado: boolean;
  // Existe versão publicada? Define se a tabela aparece com o switch Modo Edição desligado.
  possuiVersaoPublicada: boolean;
  versaoPublicada: boolean;
  // Total que atende ao filtro — o grid pagina no servidor.
  totalItens: number;
  itens: IItemPrecificacao[];
}

export interface IPrecoAlterado {
  idProduto: number;
  cor: string | null;
  preco: number;
  // Frente 10 (TP-20): Taxa dos Acabamentos. undefined = não mexer na taxa já gravada.
  taxa?: number | null;
  // Frente 11 (TP-18): combinação que identifica o preço do Kit.
  idMedidaCores?: number | null;
  idCor?: number | null;
  // Plano 08/09/2026: tamanho da barra que identifica o preço do Perfil em BR.
  tamanhoBarra?: number | null;
}

export type OperacaoMassa = "somar" | "subtrair" | "multiplicar" | "percentual";

export const OPERACAO_MASSA_LABELS: Record<OperacaoMassa, string> = {
  percentual: "Reajustar por percentual (%)",
  somar: "Somar valor fixo",
  subtrair: "Subtrair valor fixo",
  multiplicar: "Multiplicar por fator",
};

export interface IResultadoOperacao {
  sucesso: boolean;
  total: number;
  mensagem: string;
  // TP-4: sinaliza que o SALVAR devolveu a tabela para "Em Edição".
  voltouParaEdicao?: boolean;
  situacaoAtual?: SituacaoTabelaPrecoEnum;
}

// Log linha a linha devolvido pela importacao de planilha do legado.
export interface ILogImportacaoLinha {
  numeroLinhaArquivo: number;
  codigo: string;
  mensagem: string;
  // Plano 21/10/2026 (Frente 7): tamanho de barra da planilha fora do cadastro do perfil.
  tamanhoBarra?: string | null;
}

export interface ILogImportacao {
  id: number;
  logs: ILogImportacaoLinha[];
}

// Plano 14/10/2026, Frente 3: planilha do modal de importação ("Última planilha importada" e
// "Histórico de importação"). `situacao` já vem com o texto de exibição do servidor.
export interface IPlanilhaImportada {
  id: number;
  dataHora: string;
  situacao: string;
  link: string;
}

// Plano 14/10/2026, Frente 2: trava planilha × edição manual da tabela.
export interface IEstadoImportacao {
  planilhaPendente: IPlanilhaImportada | null;
  edicaoManualPendente: boolean;
}
